/**
 * Installer activation wizard.
 *
 * Mounted at /activate/:chargerCode. Token is provided via ?t= query param
 * and forwarded to every backend call. Lives outside the customer charging
 * shell on purpose — installers should not see customer chrome.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  startActivation,
  verifyOcppForActivation,
  linkChargerForActivation,
  configureChargerForActivation,
  finalizeActivation,
} from '../../api/activationApi.js';
import LocationPicker from './LocationPicker.jsx';
import './ActivationFlow.less';

// Configure-step defaults applied at mount time. Spec suggestions from the
// catalog (vendor + model) refine these for fields the installer hasn't
// touched yet. The display name always defaults to the GCEV code per
// product requirement.
const DEFAULT_SESSION_FEE     = 1;
const DEFAULT_RATE_PER_KWH    = 0.85;
const DEFAULT_RATE_PER_MINUTE = 0.175;
const DEFAULT_BILLING_UNIT    = 'kwh';
const DEFAULT_CONNECTOR_TYPE  = 'Type 2';
const DEFAULT_MAX_POWER_KW    = 22;

const STEPS = ['Start', 'Verify OCPP', 'Link', 'Configure', 'Finalize'];

// Where the backend says we should land when re-entering an in-progress
// activation. `printed` → start at step 0; `activation_pending` → after
// start (we're ready to verify OCPP); `ocpp_seen` → configure;
// `activated` → finalize / success.
const STEP_INDEX_FROM_STATUS = {
  printed: 0,
  activation_pending: 1,
  ocpp_seen: 3,
  activated: 4,
};

function errMessage(err, fallback = 'Something went wrong') {
  return (
    err?.response?.data?.message
    || (Array.isArray(err?.response?.data?.errors) && err.response.data.errors.map((e) => e.message).join('; '))
    || err?.message
    || fallback
  );
}

const ActivationFlow = () => {
  const { chargerCode: rawCode } = useParams();
  const chargerCode = decodeURIComponent(rawCode || '');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';

  const [step, setStep] = useState(0);
  const [identity, setIdentity] = useState(null);
  const [publicUrl, setPublicUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Step 2 — verify OCPP
  const [ocppState, setOcppState] = useState(null);
  const [verifying, setVerifying] = useState(false);

  // Step 3 — link
  const [linkSelection, setLinkSelection] = useState('');
  const [linkSerial, setLinkSerial] = useState('');
  const [linking, setLinking] = useState(false);

  // Step 4 — configure.
  // The display name defaults to the GCEV code; everything else gets safe
  // platform defaults that the spec-catalog suggestion (when available)
  // refines just for fields the installer hasn't manually changed.
  const [config, setConfig] = useState({
    displayName: chargerCode || '',
    locationText: '',
    lat: '',
    lng: '',
    connectorType: DEFAULT_CONNECTOR_TYPE,
    maxPowerKw: DEFAULT_MAX_POWER_KW,
    ratePerKwh: DEFAULT_RATE_PER_KWH,
    ratePerMinute: DEFAULT_RATE_PER_MINUTE,
    billingUnit: DEFAULT_BILLING_UNIT,
    sessionFee: DEFAULT_SESSION_FEE,
    minChargeAmount: 0,
    isPublished: true,
  });
  const [configuring, setConfiguring] = useState(false);

  // suggestedSpec from the catalog (vendor+model → defaults). Surfaced by
  // the backend in start + link responses once a station is linked.
  const [suggestedSpec, setSuggestedSpec] = useState(null);

  // Per-field "user has typed in this" tracking. Spec suggestions arriving
  // after a user edit must NEVER overwrite their input. Set on first
  // change of each input below.
  const touchedRef = useRef(new Set());
  const markTouched = (field) => { touchedRef.current.add(field); };
  const setField = (field, value) => {
    markTouched(field);
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  // Step 5 — finalize
  const [finalizing, setFinalizing] = useState(false);
  const [finalResult, setFinalResult] = useState(null);

  // Apply spec-catalog suggestions to fields the installer hasn't touched.
  // Called from start / link / verify responses whenever a suggestedSpec
  // arrives so the Configure step opens with sensible defaults.
  const applySuggestedSpec = useCallback((spec) => {
    if (!spec || !spec.suggested) return;
    setSuggestedSpec(spec);
    const t = touchedRef.current;
    setConfig((prev) => {
      const next = { ...prev };
      const s = spec.suggested;
      if (!t.has('connectorType') && s.connectorType) next.connectorType = s.connectorType;
      if (!t.has('maxPowerKw')    && s.maxPowerKw != null) next.maxPowerKw = s.maxPowerKw;
      if (!t.has('billingUnit')   && s.billingUnit)  next.billingUnit  = s.billingUnit;
      if (!t.has('ratePerKwh')    && s.ratePerKwh != null) next.ratePerKwh = s.ratePerKwh;
      if (!t.has('ratePerMinute') && s.ratePerMinute != null) next.ratePerMinute = s.ratePerMinute;
      return next;
    });
  }, []);

  // ─── Step 1 — start ───────────────────────────────────────────────────
  const runStart = useCallback(async () => {
    if (!token) {
      setError('Missing activation token. Open this page via the activation QR sticker on the charger.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await startActivation(chargerCode, token);
      setIdentity(result.identity);
      setPublicUrl(result.publicUrl);
      if (result.suggestedSpec) applySuggestedSpec(result.suggestedSpec);
      const seeded = STEP_INDEX_FROM_STATUS[result.identity.status] ?? 0;
      setStep(Math.max(1, seeded));
    } catch (err) {
      setError(errMessage(err, 'Could not start activation'));
    } finally {
      setLoading(false);
    }
  }, [chargerCode, token, applySuggestedSpec]);

  useEffect(() => { runStart(); }, [runStart]);

  // ─── Step 2 — verify OCPP ─────────────────────────────────────────────
  const runVerifyOcpp = async () => {
    setVerifying(true);
    setError(null);
    try {
      const result = await verifyOcppForActivation(chargerCode, token);
      setOcppState(result);
      if (result.identity) setIdentity(result.identity);
    } catch (err) {
      setError(errMessage(err, 'Could not verify OCPP'));
    } finally {
      setVerifying(false);
    }
  };

  // Auto-verify when entering step 2
  useEffect(() => {
    if (step === 1 && identity && !ocppState && !verifying) {
      runVerifyOcpp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, identity]);

  // ─── Step 3 — link ────────────────────────────────────────────────────
  const runLink = async () => {
    if (!linkSelection) {
      setError('Pick an OCPP station to link.');
      return;
    }
    setLinking(true);
    setError(null);
    try {
      const result = await linkChargerForActivation(chargerCode, token, {
        ocppIdentity: linkSelection,
        manufacturerSerial: linkSerial || null,
      });
      setIdentity(result.identity);
      if (result.suggestedSpec) applySuggestedSpec(result.suggestedSpec);
      setStep(3);
    } catch (err) {
      setError(errMessage(err, 'Link failed'));
    } finally {
      setLinking(false);
    }
  };

  // ─── Step 4 — configure ───────────────────────────────────────────────
  const runConfigure = async () => {
    setConfiguring(true);
    setError(null);
    try {
      const body = {
        ...config,
        lat: Number(config.lat),
        lng: Number(config.lng),
        maxPowerKw: Number(config.maxPowerKw),
        ratePerKwh: Number(config.ratePerKwh),
        ratePerMinute: config.ratePerMinute === '' || config.ratePerMinute == null
          ? null
          : Number(config.ratePerMinute),
        billingUnit: config.billingUnit || DEFAULT_BILLING_UNIT,
        sessionFee: Number(config.sessionFee || 0),
        minChargeAmount: Number(config.minChargeAmount || 0),
      };
      const result = await configureChargerForActivation(chargerCode, token, body);
      setIdentity(result.identity);
      setStep(4);
    } catch (err) {
      setError(errMessage(err, 'Configure failed'));
    } finally {
      setConfiguring(false);
    }
  };

  // ─── Step 5 — finalize ────────────────────────────────────────────────
  const runFinalize = async () => {
    setFinalizing(true);
    setError(null);
    try {
      const result = await finalizeActivation(chargerCode, token);
      setFinalResult(result);
      setIdentity(result.identity);
    } catch (err) {
      setError(errMessage(err, 'Finalize failed'));
    } finally {
      setFinalizing(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────

  // Pre-token check is shown immediately, even before any backend call.
  if (!token) {
    return (
      <div className="activation-shell">
        <div className="activation-container">
          <Branding />
          <div className="activation-card activation-error">
            <h1>Missing activation token</h1>
            <p>
              Open this page via the activation QR sticker on the charger. The
              link must include a <code>?t=</code> token.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="activation-shell">
        <div className="activation-container">
          <Branding />
          <div className="activation-card">
            <div className="activation-spinner-row">
              <span className="activation-spinner" /> Loading activation…
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Distinct terminal states surfaced by the backend `start` response.
  if (identity && identity.status === 'retired') {
    return (
      <div className="activation-shell">
        <div className="activation-container">
          <Branding />
          <div className="activation-card activation-error">
            <h1>Charger is retired</h1>
            <p>
              This charger identity has been retired and can no longer be activated.
              Contact GreenCharge support if this is unexpected.
            </p>
            <div className="activation-meta">{chargerCode}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="activation-shell">
      <div className="activation-container">
      <Branding />

      <header className="activation-header">
        <h1>Activate</h1>
        <div className="activation-code">{chargerCode}</div>
        {identity && (
          <span className={`activation-status status-${identity.status}`}>
            {identity.status.replace(/_/g, ' ')}
          </span>
        )}
      </header>

      <StepIndicator current={step} />

      {error && (
        <div className="activation-alert" role="alert">
          <span>{error}</span>
          <button type="button" className="activation-alert-dismiss" onClick={() => setError(null)}>
            ✕
          </button>
        </div>
      )}

      <div className="activation-body">
        {step === 0 && (
          <StepCard title="Start activation">
            <p>Press continue to begin the activation pipeline.</p>
            <button type="button" className="activation-btn primary" onClick={() => setStep(1)}>
              Continue
            </button>
          </StepCard>
        )}

        {step === 1 && (
          <StepCard title="Verify OCPP">
            {verifying && (
              <div className="activation-spinner-row">
                <span className="activation-spinner" /> Checking OCPP…
              </div>
            )}
            {!verifying && ocppState && (
              <>
                {ocppState.linked ? (
                  <>
                    <p>This identity is already linked to <code>{ocppState.station?.station_id}</code>.</p>
                    <StationCard station={ocppState.station} />
                    <button type="button" className="activation-btn primary" onClick={() => setStep(3)}>
                      Next: configure
                    </button>
                  </>
                ) : ocppState.candidates.length === 0 ? (
                  <>
                    <p>No recently-booted unlinked OCPP stations found in the last 24 hours.</p>
                    <p className="activation-muted">
                      Power on the charger and wait for it to send a BootNotification, then refresh.
                    </p>
                    <button type="button" className="activation-btn secondary" onClick={runVerifyOcpp}>
                      Refresh
                    </button>
                  </>
                ) : (
                  <>
                    <p>Select the OCPP station to link with <code>{chargerCode}</code>.</p>
                    <div className="candidate-list">
                      {ocppState.candidates.map((c) => (
                        <label
                          key={c.station_id}
                          className={`candidate ${linkSelection === c.station_id ? 'selected' : ''}`}
                        >
                          <input
                            type="radio"
                            name="candidate"
                            value={c.station_id}
                            checked={linkSelection === c.station_id}
                            onChange={() => {
                              setLinkSelection(c.station_id);
                              setLinkSerial(c.serial_number || '');
                            }}
                          />
                          <div className="candidate-body">
                            <div className="candidate-row">
                              <code>{c.station_id}</code>
                              <span className={`candidate-badge ${c.online ? 'online' : 'offline'}`}>
                                {c.online ? 'online' : c.connection_status}
                              </span>
                            </div>
                            <div className="candidate-meta">
                              {[c.vendor, c.model, c.serial_number].filter(Boolean).join(' · ') || '—'}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                    <div className="activation-actions">
                      <button type="button" className="activation-btn secondary" onClick={runVerifyOcpp}>
                        Refresh
                      </button>
                      <button
                        type="button"
                        className="activation-btn primary"
                        onClick={() => setStep(2)}
                        disabled={!linkSelection}
                      >
                        Next: link
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </StepCard>
        )}

        {step === 2 && (
          <StepCard title="Link to OCPP station">
            <form onSubmit={(e) => { e.preventDefault(); runLink(); }}>
              <Field label="OCPP identity (station_id)" required>
                <input
                  type="text"
                  value={linkSelection}
                  onChange={(e) => setLinkSelection(e.target.value)}
                  placeholder="e.g. charger001/AU105ACG00142P"
                  required
                />
              </Field>
              <Field label="Manufacturer serial (optional)">
                <input
                  type="text"
                  value={linkSerial}
                  onChange={(e) => setLinkSerial(e.target.value)}
                  placeholder="Set from BootNotification if left blank"
                />
              </Field>
              <button type="submit" className="activation-btn primary" disabled={linking}>
                {linking && <span className="activation-spinner small" />}
                Link charger
              </button>
            </form>
          </StepCard>
        )}

        {step === 3 && (
          <StepCard title="Configure">
            {suggestedSpec && suggestedSpec.suggested && (
              <div className="activation-spec-hint">
                <span className="activation-spec-hint-tag">
                  {suggestedSpec.matchType === 'exact' ? 'Matched spec' : 'Vendor match'}
                </span>
                <span>
                  Defaults pre-filled from{' '}
                  <strong>{suggestedSpec.vendor || '—'} {suggestedSpec.model || ''}</strong>{' '}
                  spec — change any field below to override.
                </span>
              </div>
            )}
            <form onSubmit={(e) => { e.preventDefault(); runConfigure(); }}>
              <div className="form-row">
                <Field label="Display name" required>
                  <input
                    value={config.displayName}
                    required
                    onChange={(e) => setField('displayName', e.target.value)}
                  />
                </Field>
                <Field label="Location" required>
                  <input
                    value={config.locationText}
                    required
                    onChange={(e) => setField('locationText', e.target.value)}
                  />
                </Field>
              </div>

              <LocationPicker
                value={{ lat: config.lat, lng: config.lng, locationText: config.locationText }}
                onChange={({ lat, lng, locationText }) => {
                  // marker movement bumps coordinates + a suggested
                  // locationText; both pass through setField so user edits
                  // remain protected if they typed the address first.
                  setConfig((prev) => {
                    const t = touchedRef.current;
                    const next = { ...prev };
                    if (lat != null)  next.lat = lat;
                    if (lng != null)  next.lng = lng;
                    // Only update locationText from reverse-geocoding if
                    // the installer hasn't typed something into it yet.
                    if (locationText && !t.has('locationText')) {
                      next.locationText = locationText;
                    }
                    return next;
                  });
                  // The map is the source of truth for coordinates while
                  // it's the active editor; mark them touched so manual
                  // edits below still count.
                  touchedRef.current.add('lat');
                  touchedRef.current.add('lng');
                }}
              />

              <div className="form-row">
                <Field label="Latitude" required>
                  <input
                    type="number" step="any" required
                    value={config.lat}
                    onChange={(e) => setField('lat', e.target.value)}
                  />
                </Field>
                <Field label="Longitude" required>
                  <input
                    type="number" step="any" required
                    value={config.lng}
                    onChange={(e) => setField('lng', e.target.value)}
                  />
                </Field>
              </div>
              <div className="form-row">
                <Field label="Connector type">
                  <select
                    value={config.connectorType}
                    onChange={(e) => setField('connectorType', e.target.value)}
                  >
                    <option>Type 2</option>
                    <option>CCS2</option>
                    <option>CHAdeMO</option>
                    <option>Type 1</option>
                  </select>
                </Field>
                <Field label="Max power (kW)" required>
                  <input
                    type="number" step="0.1" min="0" required
                    value={config.maxPowerKw}
                    onChange={(e) => setField('maxPowerKw', e.target.value)}
                  />
                </Field>
              </div>

              {/* Billing mode toggle. Both rates are kept in state so toggling
                  back and forth is lossless. Only the active mode's rate
                  appears as an input. */}
              <div className="form-row activation-billing-toggle-row">
                <label className="activation-field-label">Billing mode</label>
                <div className="activation-billing-toggle" role="radiogroup" aria-label="Billing mode">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={config.billingUnit === 'kwh'}
                    className={`activation-billing-pill ${config.billingUnit === 'kwh' ? 'is-active' : ''}`}
                    onClick={() => setField('billingUnit', 'kwh')}
                  >
                    Per kWh
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={config.billingUnit === 'minute'}
                    className={`activation-billing-pill ${config.billingUnit === 'minute' ? 'is-active' : ''}`}
                    onClick={() => setField('billingUnit', 'minute')}
                  >
                    Per minute
                  </button>
                </div>
              </div>

              <div className="form-row">
                {config.billingUnit === 'minute' ? (
                  <Field label="Rate per minute" required>
                    <input
                      type="number" step="0.001" min="0" required
                      value={config.ratePerMinute}
                      onChange={(e) => setField('ratePerMinute', e.target.value)}
                    />
                  </Field>
                ) : (
                  <Field label="Rate per kWh" required>
                    <input
                      type="number" step="0.01" min="0" required
                      value={config.ratePerKwh}
                      onChange={(e) => setField('ratePerKwh', e.target.value)}
                    />
                  </Field>
                )}
                <Field label="Session fee">
                  <input
                    type="number" step="0.01" min="0"
                    value={config.sessionFee}
                    onChange={(e) => setField('sessionFee', e.target.value)}
                  />
                </Field>
              </div>

              <label className="activation-switch">
                <input
                  type="checkbox"
                  checked={config.isPublished}
                  onChange={(e) => setField('isPublished', e.target.checked)}
                />
                <span>Publish station as publicly available after activation</span>
              </label>
              <button type="submit" className="activation-btn primary" disabled={configuring}>
                {configuring && <span className="activation-spinner small" />}
                Save configuration
              </button>
            </form>
          </StepCard>
        )}

        {step === 4 && (
          <StepCard title="Review & finalize">
            {finalResult?.alreadyActivated || identity?.status === 'activated' ? (
              <div className="activation-success">
                <div className="activation-success-icon">✓</div>
                <h2>Charger connected successfully</h2>
                <p className="activation-muted">
                  Your charger is ready for its final automated test.
                </p>
                {finalResult?.installerTest?.installerUrl ? (
                  <div className="installer-test-cta" style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <a
                      href={finalResult.installerTest.installerUrl}
                      className="activation-btn success"
                      style={{ textAlign: 'center' }}
                    >
                      Begin charger test
                    </a>
                    <p className="activation-muted" style={{ fontSize: '0.85em' }}>
                      Save this secure link to continue later — it stays valid until{' '}
                      {finalResult.installerTest.expiresAt
                        ? new Date(finalResult.installerTest.expiresAt).toLocaleDateString()
                        : 'the configured expiry'}
                      .
                    </p>
                  </div>
                ) : null}
                <div className="public-url" style={{ marginTop: 20 }}>
                  <span className="public-url-label">Public charging URL</span>
                  <a
                    href={finalResult?.publicUrl || publicUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="public-url-value"
                  >
                    {finalResult?.publicUrl || publicUrl}
                  </a>
                </div>
              </div>
            ) : (
              <>
                <p>About to flip <code>{chargerCode}</code> to <strong>activated</strong>.</p>
                <p className="activation-muted">
                  This consumes the activation token, mirrors the GCEV code into the station record,
                  and makes the public charging QR usable. Safe to retry on failure.
                </p>
                <button
                  type="button"
                  className="activation-btn success"
                  onClick={runFinalize}
                  disabled={finalizing}
                >
                  {finalizing && <span className="activation-spinner small" />}
                  Activate charger
                </button>
              </>
            )}
          </StepCard>
        )}
      </div>
      </div>
    </div>
  );
};

// ─── Subcomponents ──────────────────────────────────────────────────────

const Branding = () => (
  <div className="activation-branding">
    <span className="activation-logo-dot" />
    GreenCharge <span className="activation-logo-suffix">installer</span>
  </div>
);

const StepIndicator = ({ current }) => (
  <ol className="step-indicator">
    {STEPS.map((label, idx) => (
      <li
        key={label}
        className={`step ${idx < current ? 'done' : ''} ${idx === current ? 'current' : ''}`}
      >
        <span className="step-num">{idx < current ? '✓' : idx + 1}</span>
        <span className="step-label">{label}</span>
      </li>
    ))}
  </ol>
);

const StepCard = ({ title, children }) => (
  <div className="step-card">
    <h2>{title}</h2>
    {children}
  </div>
);

const Field = ({ label, required, children }) => (
  <label className="activation-field">
    <span className="activation-field-label">
      {label}{required && <span className="activation-field-required">*</span>}
    </span>
    {children}
  </label>
);

const StationCard = ({ station }) => {
  if (!station) return null;
  return (
    <div className="station-card">
      <div className="station-row">
        <code>{station.station_id}</code>
        <span className={`candidate-badge ${station.online ? 'online' : 'offline'}`}>
          {station.online ? 'online' : station.connection_status}
        </span>
      </div>
      <div className="station-meta">
        {[station.vendor, station.model, station.serial_number].filter(Boolean).join(' · ') || '—'}
      </div>
    </div>
  );
};

export default ActivationFlow;
